import * as React from "react";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline';
  size?: 'sm' | 'md';
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className = "", variant = 'default', size = 'sm', children, ...props }, ref) => {
    const baseStyles = "inline-flex items-center font-medium rounded-full";
    
    const sizeStyles = {
      sm: "px-2 py-0.5 text-[10px]",
      md: "px-2.5 py-1 text-xs"
    };

    const variantStyles = {
      default: "bg-neutral-100 text-neutral-800",
      secondary: "bg-neutral-50 text-neutral-600 border border-neutral-200",
      success: "bg-emerald-100 text-emerald-800",
      warning: "bg-amber-100 text-amber-800",
      danger: "bg-red-100 text-red-800",
      outline: "bg-transparent border border-neutral-300 text-neutral-700"
    };

    return (
      <span
        ref={ref}
        className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = "Badge";

// Priority Badge specifically for fundraising
export interface PriorityBadgeProps {
  priority: 'tier_1' | 'tier_2' | 'tier_3';
  showLabel?: boolean;
  className?: string;
}

export const PriorityBadge = ({ priority, showLabel = true, className = "" }: PriorityBadgeProps) => {
  const config = {
    tier_1: { color: 'bg-amber-400', label: 'Tier 1' },
    tier_2: { color: 'bg-slate-400', label: 'Tier 2' },
    tier_3: { color: 'bg-slate-300', label: 'Tier 3' }
  };

  const { color, label } = config[priority];

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {showLabel && <span className="text-xs font-medium text-neutral-700">{label}</span>}
    </span>
  );
};
